// src/lib/schema/components/CollectionSchema.tsx

import React from 'react';
import { CollectionSchemaParams, generateCollectionSchema, schemaToString } from '@/lib/schema';
import { ProductData } from '../types';

/**
 * Props for the CollectionSchema component
 */
interface CollectionSchemaProps {
  title: string;
  subtitle?: string;
  lang: string;
  searchParams?: Record<string, string>;
  products?: ProductData[];
  image?: string;
  description?: string;
  baseUrl?: string;
  lastModified?: string;
}

/**
 * Component that renders the JSON-LD schema for a collection page
 */
export function CollectionSchema({
  title,
  subtitle,
  lang,
  searchParams = {},
  products = [],
  image,
  description,
  baseUrl,
  lastModified
}: CollectionSchemaProps): React.ReactElement | null {
  // Return null if required props are missing
  if (!title || !lang) return null;

  // Prepare parameters for collection schema
  const params: CollectionSchemaParams = {
    pageTitle: title,
    pageSubtitle: subtitle,
    searchParams,
    products,
    baseUrl,
    lastModified
  };

  // Add optional parameters if provided
   if (image) params.image = image;
   if (description) params.description = description;

  // Generate the collection schema
  const collectionSchema = generateCollectionSchema(params, lang);

  // Return null if schema generation failed
  if (!collectionSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(collectionSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}

/**
 * Props for the FilteredCo        llectionSchema component
 */
interface FilteredCollectionSchemaProps {
  filterType: string;
  filterValue: string;
  lang: string;
  products?: ProductData[];
  baseUrl?: string;
  lastModified?: string;
}

/**
 * Component that renders the JSON-LD schema for a filtered collection page
 */
export function FilteredCollectionSchema({
  filterType,
  filterValue,
  lang,
  products = [],
  baseUrl,
  lastModified
}: FilteredCollectionSchemaProps): React.ReactElement | null {
  // Return null if required props are missing
  if (!filterType || !filterValue || !lang) return null;

  // Import the necessary function dynamically
  const { generateFilteredCollectionSchema } = require('../base/collection');

  // Prepare parameters for filtered collection schema
  const params = {
    filterType,
    filterValue,
    products,
    baseUrl,
    lastModified
  };

  // Generate the filtered collection schema
  const filteredSchema = generateFilteredCollectionSchema(params, lang);

  // Return null if schema generation failed
  if (!filteredSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(filteredSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}